import { useEffect, useState, useCallback } from 'react';
import { ClockCounterClockwise, MagnifyingGlass } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useT } from '@/i18n/LanguageContext';
import { shizaiApi, ORDER_STATUS_BADGE, yen } from './shizaiApi';
import { DataTable } from '@/components/ui/DataTable';

export default function PurchaseHistory() {
  const { t } = useT();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');

  const load = useCallback(() => {
    shizaiApi.history(q.trim() ? { q: q.trim() } : {}).then(setRows).catch(() => {});
  }, [q]);
  useEffect(() => { const id = setTimeout(load, 250); return () => clearTimeout(id); }, [load]);

  return (
    <div className="space-y-5 page-enter" data-testid="shizai-history">
      <h1 className="text-2xl font-bold tracking-tight text-zinc-900">{t('shizai.history.title')}</h1>

      <div className="relative max-w-sm">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
        <Input data-testid="history-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('shizai.history.searchPh')} className="h-10 pl-9 rounded-lg" />
      </div>

      <DataTable
        dataTestId="history-table"
        rows={rows}
        rowKey={(r, i) => `${r.order_no}-${i}`}
        empty={{ icon: <ClockCounterClockwise size={40} weight="duotone" className="text-zinc-300 mx-auto mb-2" />, text: t('shizai.history.empty'), testid: 'history-empty' }}
        columns={[
          { key: 'order_no', header: t('shizai.order.no'), width: 'w-32', mono: true, cell: (r) => <span className="font-mono text-xs text-zinc-500">{r.order_no}</span> },
          { key: 'order_date', header: t('shizai.order.orderDate'), width: 'w-28', cell: (r) => <span className="text-zinc-600">{r.order_date || '—'}</span> },
          { key: 'supplier', header: t('shizai.order.supplier'), flex: true, cell: (r) => <span className="min-w-0 text-zinc-700 line-clamp-2 break-words" title={r.supplier_name || ''}>{r.supplier_name || '—'}</span> },
          { key: 'part_no', header: t('shizai.req.partNo'), width: 'w-28', mono: true, cell: (r) => <span className="font-mono text-xs text-zinc-600">{r.part_no || '—'}</span> },
          { key: 'name', primary: true, header: t('shizai.req.name'), flex: true, cell: (r) => <span className="min-w-0 font-medium text-zinc-800 line-clamp-2 break-words" title={r.name || ''}>{r.name || '—'}</span> },
          { key: 'qty', header: t('shizai.req.qty'), width: 'w-24', align: 'right', cell: (r) => <span className="tabular-nums text-zinc-700">{r.qty} {r.uom}</span> },
          { key: 'unit_price', header: t('shizai.order.unitPrice'), width: 'w-28', align: 'right', cell: (r) => <span className="tabular-nums text-zinc-700">{yen(r.unit_price)}</span> },
          { key: 'amount', header: t('shizai.order.amount'), width: 'w-28', align: 'right', cell: (r) => <span className="tabular-nums font-medium text-zinc-800">{yen(r.amount)}</span> },
          { key: 'status', header: '', width: 'w-24', cell: (r) => <Badge variant="outline" className={ORDER_STATUS_BADGE[r.status]}>{t(`shizai.orderStatus.${r.status}`)}</Badge> },
        ]}
      />
    </div>
  );
}
